import flow from "lodash/fp/flow";
import getOr from "lodash/fp/getOr";
import omit from "lodash/fp/omit";
import split from "lodash/fp/split";

import selectUserChoiceValues from "./selectUserChoiceValues";
import selectSelectedProduct from "./selectSelectedProduct";
import selectProductSku from "./selectProductSku";
import selectMaterialID from "./selectMaterialID";
import { getGoalieGearAddToOrderValues } from "utils";

const ignoreValuesFor = [
  "SkateCustomiseBlade",
  "customizeBladePlayerName",
  "customizeBladePlayerNumber",
  "customizeBladeProfile",
  "customizeBladeSharpening",
  "playerName",
  "playerNumber",
  "size_left",
  "size_right",
  "noPersonalization",
  "customizeBladeNoPersonalization",
  "addExtension",
  "engineeredFor",
];

const playerStickColourCodeMap = {
  RED: "D001",
  BLUE: "D002",
  GREEN: "D003",
  ORANGE: "D006",
  MAROON: "D007",
  SILVER: "D013",
  YELLOW: "D022",
  CANADA: "D023",
  USA: "D024",
  BLACK: "D050",
  PURPLE: "D053",
  TEAL: "D052",
  RETAIL: "C001",
  "WHITE/BLACK": "D011",
  "WHITE/NAVY": "C002",
  "WHITE/ORANGE": "D006",
  "WHITE/GREEN": "D003",
  "WHITE/METALLIC GOLD": "D023",
  "WHITE/SPORT GOLD": "D010",
  "WHITE-/MAROON": "D007",
  "WHITE/WHITE": "D027",
  "WHITE/TEAL": "D052",
  "WHITE/PURPLE": "D053",
  "WHITE/BLUE": "D002",
  "WHITE/RED": "C001",
  "BLACK/BLACK": "D050",
};

function getSizeFromFlex(flex) {
  return {
    40: "Junior",
    "Flex 40": "Junior",
    50: "Junior",
    "Flex 50": "Junior",
    55: "Intermediate",
    "Flex 55": "Intermediate",
    65: "Intermediate",
    "Flex 65": "Intermediate",
    "65 SR": "Senior",
    "Flex 65 SR": "Senior",
    "65S": "Senior",
    70: "Senior",
    "Flex 70": "Senior",
    77: "Senior",
    "Flex 77": "Senior",
    82: "Senior",
    "Flex 82": "Senior",
    87: "Senior",
    "Flex 87": "Senior",
    95: "Senior",
    "Flex 95": "Senior",
    102: "Senior",
    "Flex 102": "Senior",
    112: "Senior",
    "Flex 112": "Senior",
  }[flex];
}

function getProductSpecificAddToOrderValues(state) {
  const product = selectSelectedProduct(state);
  const productSku = selectProductSku(state);
  const materialID = selectMaterialID(state);
  const userChoiceValues = selectUserChoiceValues(state);
  const [playerNameFirst = "", playerNameLast = ""] = flow(
    getOr([], ["app", "volumentalData", "user_profile", "name"]),
    split(" ")
  )(state);
  const { external_id, device_id } = getOr({}, [
    "app",
    "volumentalData",
    "scan",
  ])(state);

  const fromVolumentalData = {
    "Vandra Scanner Number": device_id,
    "Unique Foot Scan ID": external_id,
    "Player Name, First": playerNameFirst,
    "Player Name, Last": playerNameLast,
  };

  switch (product?.productFamily) {
    case "HyperliteGoalieStick2023":
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "Personalization (player name)": userChoiceValues.playerName,
        "Shaft Color text": userChoiceValues["Shaft Color code"],
        "Paddle Color text": userChoiceValues["Paddle Color code"],
        "Shoulder Decal Color text":
          userChoiceValues["Shoulder Decal Color code"],
        "Bauer Logo Color text": userChoiceValues["Bauer Logo Color code"],
        "HYPERLITE Logo Color text":
          userChoiceValues["HYPERLITE Logo Color code"],
        "Stick Extension": userChoiceValues["Stick Extension"]
          ? 'Add 2" extension'
          : "Stock",
        PentaGrip: userChoiceValues["PentaGrip"] ? "Remove Pentagrip" : "Stock",
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "MachGoalieStick2022":
      const shaftColorCode = [
        userChoiceValues["Shaft Color code"],
        userChoiceValues["Shoulder Color code"],
      ]
        .filter(Boolean)
        .join("_");
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Shaft Color code": shaftColorCode,
        "Shaft Color text": shaftColorCode,
        "Paddle Color text": userChoiceValues["Paddle Color code"],
        "Shoulder Decal Color text":
          userChoiceValues["Shoulder Decal Color code"],
        "Outline Color text": userChoiceValues["Outline Color code"],
        "Spine Color text": userChoiceValues["Spine Color code"],
        PentaGrip: userChoiceValues["PentaGrip"] ? "Add Pentagrip" : "Stock",
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "ProCustomPlayerStick2023":
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        Graphic: userChoiceValues.Graphic,
        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
        Size: getSizeFromFlex(userChoiceValues.Flex),
        "Hand (aka curve or handedness)": userChoiceValues.Hand,
        "End Plug (aka extension)": userChoiceValues.addExtension
          ? ["ER Spine", "XE Taper"].includes(userChoiceValues.Taper)
            ? 'Add 2" Extension'
            : 'Add 4" Extension'
          : "Stock",
        "End Plug Code": userChoiceValues.addExtension
          ? ["ER Spine", "XE Taper"].includes(userChoiceValues.Taper)
            ? "2"
            : "4"
          : "No",
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "AG5NTPlayerStick2023":
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "Hand (aka curve or handedness)": userChoiceValues.Hand,
        "End Plug (aka extension)": userChoiceValues.addExtension
          ? 'Add 4" Extension'
          : "Stock",
        "End Plug Code": userChoiceValues.addExtension ? "4" : "No",
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
        Size: getSizeFromFlex(userChoiceValues.Flex),
      };

    case "HyperlitePlayerStick2023":
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "Hand (aka curve or handedness)": userChoiceValues.Hand,
        "End Plug (aka extension)": userChoiceValues.addExtension
          ? 'Add 2" Extension'
          : "Stock",
        "End Plug Code": userChoiceValues.addExtension ? "2" : "No",
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
        Size: getSizeFromFlex(userChoiceValues.Flex),
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "NexusGeoPlayerStick2020":
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "Hand (aka curve or handedness)": userChoiceValues.Hand,
        "End Plug (aka extension)": userChoiceValues.addExtension
          ? 'Add 2" Extension'
          : "Stock",
        "End Plug Code": userChoiceValues.addExtension ? "2" : "No",
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
        Size: getSizeFromFlex(userChoiceValues.Flex),
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "SupremePlayerStick2020":
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "End Plug (aka extension)": userChoiceValues.addExtension
          ? 'Add 4" Extension'
          : "Stock",
        "End Plug Code": userChoiceValues.addExtension ? "4" : "No",
        "Hand (aka curve or handedness)": userChoiceValues.Hand,
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
        Size: getSizeFromFlex(userChoiceValues.Flex),
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "Hyperlite2Skate2023":
      return {
        productSku,
        product: state?.app?.formData?.product,
        SKU: productSku,
        ProductFamily: product?.productFamily,
        ...fromVolumentalData,
        ...userChoiceValues,
        "Number for Monogramming": userChoiceValues.playerNumber,
        "Name for Monogramming": userChoiceValues.playerName,
        "Blade Name": userChoiceValues.customizeBladePlayerName,
        "Blade Number": userChoiceValues.customizeBladePlayerNumber,
        "Left Length": userChoiceValues.size_left,
        "Right Length": userChoiceValues.size_right,
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "CustomSteel2023":
      return {
        productSku,
        product: state?.app?.formData?.product,
        SKU: productSku,
        ProductFamily: product?.productFamily,
        ...userChoiceValues,
        "Blade Name": userChoiceValues.customizeBladePlayerName,
        "Blade Number": userChoiceValues.customizeBladePlayerNumber,
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "SupremeMachSkate2022":
      return {
        productSku,
        product: state?.app?.formData?.product,
        SKU: productSku,
        ProductFamily: product?.productFamily,
        ...fromVolumentalData,
        ...userChoiceValues,
        "Number for Monogramming": userChoiceValues.playerNumber,
        "Name for Monogramming": userChoiceValues.playerName,
        "Blade Name": userChoiceValues.customizeBladePlayerName,
        "Blade Number": userChoiceValues.customizeBladePlayerNumber,
        "Left Length": userChoiceValues.size_left,
        "Right Length": userChoiceValues.size_right,
        Eyelets: "Standard",
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "QuickTurnVaporStick":
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "Hand (aka curve or handedness)": userChoiceValues.Hand,
        'End Plug (aka 4" extension)': userChoiceValues.addExtension
          ? 'Add 4" Extension'
          : "Stock",
        "End Plug Code": userChoiceValues.addExtension ? "4" : "No",
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
        Size: getSizeFromFlex(userChoiceValues.Flex),
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "QuickTurnSupremeStick":
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "Hand (aka curve or handedness)": userChoiceValues.Hand,
        'End Plug (aka 4" extension)': userChoiceValues.addExtension
          ? 'Add 4" Extension'
          : "Stock",
        "End Plug Code": userChoiceValues.addExtension ? "4" : "No",
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
        Size: getSizeFromFlex(userChoiceValues.Flex),
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "QuickTurnNexusStick":
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        "Hand (aka curve or handedness)": userChoiceValues.Hand,
        'End Plug (aka 4" extension)': userChoiceValues.addExtension
          ? 'Add 2" Extension'
          : "Stock",
        "End Plug Code": userChoiceValues.addExtension ? "2" : "No",
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
        Size: getSizeFromFlex(userChoiceValues.Flex),
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "ProCustomGoalierGear2023":
    case "VaporHyperliteGoalierGear2023":
    case "SupremeMachGoalierGear2023":
      // console.log(`${product?.productFamily} RECIPE VALUES`, {
      //   ...getGoalieGearAddToOrderValues({
      //     userChoiceValues,
      //     product,
      //     materialID,
      //     state,
      //   }),
      //   webAppValues: JSON.stringify(userChoiceValues),
      // });
      return {
        ...getGoalieGearAddToOrderValues({
          userChoiceValues,
          product,
          materialID,
          state,
        }),
        webAppValues: JSON.stringify(userChoiceValues),
      };

    default:
      return {
        productSku,
        Family: product?.family,
        ProductFamily: product?.productFamily,
        product: state?.app?.formData?.product,
        ...userChoiceValues,
        webAppValues: JSON.stringify(userChoiceValues),
      };
  }
}

const selectAddToOrderValues = flow(
  getProductSpecificAddToOrderValues,
  omit(ignoreValuesFor)
);

export default selectAddToOrderValues;
